import React, { useState, useEffect } from 'react';
import { useNotificaciones } from '../context/NotificacionesContext';
import './Pedidos.css';

function Pedidos({ usuario }) {
  const [vistaActual, setVistaActual] = useState('pedidos'); // 'pedidos' o 'cotizaciones'
  const [pedidos, setPedidos] = useState([]);
  const [cotizaciones, setCotizaciones] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [estadisticas, setEstadisticas] = useState({});
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [pedidoSeleccionado, setPedidoSeleccionado] = useState(null);
  const [cotizacionSeleccionada, setCotizacionSeleccionada] = useState(null);
  const [actualizando, setActualizando] = useState(false);
  
  const { refrescarNotificaciones, pedidosNuevos, cotizacionesNuevas } = useNotificaciones();

  // Cargar pedidos y cotizaciones
  useEffect(() => {
    cargarPedidos();
    cargarEstadisticas();
    cargarCotizaciones();
    // Refrescar notificaciones al entrar a la vista
    refrescarNotificaciones();
  }, []);

  const cargarPedidos = async () => {
    try {
      setCargando(true);
      const resp = await fetch('/api/pedidos');
      if (!resp.ok) throw new Error('Error al cargar pedidos');
      const data = await resp.json();
      setPedidos(data);
    } catch (e) {
      alert('Error al cargar pedidos: ' + e.message);
    } finally {
      setCargando(false);
    }
  };

  const cargarEstadisticas = async () => {
    try {
      const resp = await fetch('/api/pedidos/estadisticas');
      if (resp.ok) {
        const data = await resp.json();
        setEstadisticas(data);
      }
    } catch (e) {
      console.error('Error al cargar estadísticas:', e);
    }
  };

  const cargarCotizaciones = async () => {
    try {
      const resp = await fetch('/api/ecommerce/cotizaciones');
      if (!resp.ok) throw new Error('Error al cargar cotizaciones');
      const data = await resp.json();
      setCotizaciones(data);
    } catch (e) {
      console.error('Error al cargar cotizaciones:', e);
      setCotizaciones([]);
    }
  };

  const actualizarEstadoCotizacion = async (idCotizacion, nuevoEstado) => {
    if (actualizando) return;
    setActualizando(true);
    try {
      const resp = await fetch(`/api/ecommerce/cotizaciones/${idCotizacion}/estado`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ estado: nuevoEstado })
      });
      
      if (!resp.ok) throw new Error('Error al actualizar estado');
      
      alert('Estado actualizado correctamente');
      cargarCotizaciones();
    } catch (e) {
      alert('Error al actualizar estado: ' + e.message);
    } finally {
      setActualizando(false);
    }
  };

  const cambiarEstadoPedido = async (pedidoId, nuevoEstado) => {
    if (actualizando) return;
    setActualizando(true);
    try {
      const resp = await fetch(`/api/pedidos/${pedidoId}/estado`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ estado_pedido: nuevoEstado })
      });
      
      if (!resp.ok) throw new Error('Error al actualizar estado');
      
      const pedidoActualizado = await resp.json();
      setPedidos(prev => prev.map(p => p.id_pedido === pedidoId ? pedidoActualizado : p));
      cargarEstadisticas();
    } catch (e) {
      alert('Error al actualizar estado: ' + e.message);
    } finally {
      setActualizando(false);
    }
  };

  const convertirAVenta = async (pedidoId) => {
    if (!window.confirm('¿Convertir este pedido a venta? Se descontará el stock automáticamente.')) return;
    setActualizando(true);
    try {
      const resp = await fetch(`/api/pedidos/${pedidoId}/convertir-venta`, {
        method: 'POST'
      });
      
      if (!resp.ok) {
        const error = await resp.json();
        throw new Error(error.error || 'Error al convertir pedido');
      }
      
      const data = await resp.json();
      alert(`Pedido convertido a venta #${data.id_venta} exitosamente`);
      cargarPedidos();
      cargarEstadisticas();
    } catch (e) {
      alert(e.message);
    } finally {
      setActualizando(false);
    }
  };

  const cancelarPedido = async (pedidoId) => {
    const motivo = prompt('Motivo de cancelación (opcional):');
    if (motivo === null) return;
    
    setActualizando(true);
    try {
      const resp = await fetch(`/api/pedidos/${pedidoId}/cancelar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ motivo })
      });
      
      if (!resp.ok) throw new Error('Error al cancelar pedido');
      
      alert('Pedido cancelado');
      cargarPedidos();
      cargarEstadisticas();
    } catch (e) {
      alert('Error: ' + e.message);
    } finally {
      setActualizando(false);
    }
  };

  const pedidosFiltrados = pedidos.filter(pedido =>
    filtroEstado === 'todos' || pedido.estado_pedido === filtroEstado
  );

  const getEstadoColor = estado => {
    switch(estado) {
      case 'nuevo': return '#ff9800';
      case 'confirmado': return '#2196f3';
      case 'preparando': return '#9c27b0';
      case 'enviado': return '#00bcd4';
      case 'entregado': return '#4caf50';
      case 'cancelado': return '#f44336';
      default: return '#757575';
    }
  };

  const getEstadoTexto = estado => {
    switch(estado) {
      case 'nuevo': return 'Nuevo';
      case 'confirmado': return 'Confirmado';
      case 'preparando': return 'Preparando';
      case 'enviado': return 'Enviado';
      case 'entregado': return 'Entregado';
      case 'cancelado': return 'Cancelado';
      default: return estado;
    }
  };

  const getEstadoCotizacionColor = estado => {
    switch(estado) {
      case 'pendiente': return '#ff9800';
      case 'contactado': return '#2196f3';
      case 'convertido': return '#4caf50';
      case 'rechazado': return '#f44336';
      default: return '#757575';
    }
  };

  const getEstadoCotizacionTexto = estado => {
    switch(estado) {
      case 'pendiente': return 'Pendiente';
      case 'contactado': return 'Contactado';
      case 'convertido': return 'Convertido';
      case 'rechazado': return 'Rechazado';
      default: return estado;
    }
  };

  const formatFecha = (fecha) => {
    return new Date(fecha).toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (cargando) {
    return <div className="pedidos-container"><p>Cargando pedidos...</p></div>;
  }

  return (
    <div className="pedidos-container">
      <h2>Gestión de Pedidos y Cotizaciones</h2>

      {/* Banner de notificaciones */}
      {(pedidosNuevos > 0 || cotizacionesNuevas > 0) && (
        <div style={{
          background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
          color: '#fff',
          padding: '12px 20px',
          borderRadius: '8px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          boxShadow: '0 4px 12px rgba(238, 90, 111, 0.3)',
          animation: 'slideDown 0.5s ease-out'
        }}>
          <span style={{fontSize: '24px'}}>🔔</span>
          <div style={{flex: 1}}>
            {pedidosNuevos > 0 && <div><strong>{pedidosNuevos}</strong> pedido{pedidosNuevos !== 1 ? 's' : ''} nuevo{pedidosNuevos !== 1 ? 's' : ''}</div>}
            {cotizacionesNuevas > 0 && <div><strong>{cotizacionesNuevas}</strong> cotización{cotizacionesNuevas !== 1 ? 'es' : ''} pendiente{cotizacionesNuevas !== 1 ? 's' : ''}</div>}
          </div>
          <button 
            onClick={refrescarNotificaciones}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: '1px solid rgba(255,255,255,0.3)',
              color: '#fff',
              padding: '6px 12px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 'bold'
            }}
          >
            🔄 Actualizar
          </button>
        </div>
      )}

      {/* Pestañas */}
      <div style={{display:'flex', gap:'10px', marginBottom:'20px', borderBottom:'2px solid #e0e0e0', paddingBottom:'10px'}}>
        <button
          onClick={() => setVistaActual('pedidos')}
          style={{
            padding: '10px 20px',
            background: vistaActual === 'pedidos' ? '#0d1846' : 'transparent',
            color: vistaActual === 'pedidos' ? '#fff' : '#0d1846',
            border: 'none',
            borderBottom: vistaActual === 'pedidos' ? '3px solid #0d1846' : 'none',
            cursor: 'pointer',
            fontWeight: vistaActual === 'pedidos' ? 'bold' : 'normal',
            fontSize: '16px',
            position: 'relative'
          }}
        >
          📦 Pedidos E-commerce
          {pedidosNuevos > 0 && (
            <span style={{
              position: 'absolute',
              top: '4px',
              right: '4px',
              background: '#e74c3c',
              color: '#fff',
              borderRadius: '50%',
              width: '18px',
              height: '18px',
              fontSize: '10px',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {pedidosNuevos}
            </span>
          )}
        </button>
        <button
          onClick={() => setVistaActual('cotizaciones')}
          style={{
            padding: '10px 20px',
            background: vistaActual === 'cotizaciones' ? '#0d1846' : 'transparent',
            color: vistaActual === 'cotizaciones' ? '#fff' : '#0d1846',
            border: 'none',
            borderBottom: vistaActual === 'cotizaciones' ? '3px solid #0d1846' : 'none',
            cursor: 'pointer',
            fontWeight: vistaActual === 'cotizaciones' ? 'bold' : 'normal',
            fontSize: '16px',
            position: 'relative'
          }}
        >
          📋 Cotizaciones E-commerce
          {cotizacionesNuevas > 0 && (
            <span style={{
              position: 'absolute',
              top: '4px',
              right: '4px',
              background: '#e74c3c',
              color: '#fff',
              borderRadius: '50%',
              width: '18px',
              height: '18px',
              fontSize: '10px',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {cotizacionesNuevas}
            </span>
          )}
        </button>
      </div>

      {vistaActual === 'pedidos' ? (
        <>
          {/* Filtros */}
          <div className="pedidos-filtros">
            <label>Filtrar por estado:</label>
            <select value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)}>
              <option value="todos">Todos</option>
              <option value="nuevo">Nuevos</option>
              <option value="confirmado">Confirmados</option>
              <option value="preparando">Preparando</option>
              <option value="enviado">Enviados</option>
              <option value="entregado">Entregados</option>
              <option value="cancelado">Cancelados</option>
            </select>
            <button onClick={cargarPedidos} className="btn-confirmar" style={{marginLeft: '10px'}}>
              🔄 Actualizar
            </button>
          </div>

      {/* Estadísticas */}
      <div className="pedidos-stats">
        <div className="stat-card" style={{ backgroundColor: '#fff', color: '#0d1846' }}>
          <h4>Total Pedidos</h4>
          <p>{estadisticas.total_pedidos || 0}</p>
        </div>
        <div className="stat-card" style={{ backgroundColor: '#fff', color: '#0d1846' }}>
          <h4>Nuevos</h4>
          <p>{estadisticas.nuevos || 0}</p>
        </div>
        <div className="stat-card" style={{ backgroundColor: '#fff', color: '#0d1846' }}>
          <h4>Confirmados</h4>
          <p>{estadisticas.confirmados || 0}</p>
        </div>
        <div className="stat-card" style={{ backgroundColor: '#fff', color: '#0d1846' }}>
          <h4>Preparando</h4>
          <p>{estadisticas.preparando || 0}</p>
        </div>
        <div className="stat-card" style={{ backgroundColor: '#fff', color: '#0d1846' }}>
          <h4>Entregados</h4>
          <p>{estadisticas.entregados || 0}</p>
        </div>
        <div className="stat-card" style={{ backgroundColor: '#fff', color: '#0d1846' }}>
          <h4>Valor Total</h4>
          <p>${Number(estadisticas.total_ventas || 0).toFixed(2)}</p>
        </div>
      </div>

      {/* Lista de pedidos */}
      <div className="pedidos-lista">
        {pedidosFiltrados.length === 0 ? (
          <p style={{textAlign: 'center', padding: '20px', color: '#666'}}>
            No hay pedidos {filtroEstado !== 'todos' ? `en estado "${getEstadoTexto(filtroEstado)}"` : ''}
          </p>
        ) : (
          pedidosFiltrados.map(pedido => (
          <div key={pedido.id_pedido} className="pedido-card">
            <div className="pedido-header">
              <div className="pedido-info">
                <h3>Pedido #{pedido.id_pedido}</h3>
                <p>Fecha: {formatFecha(pedido.fecha_creacion)}</p>
                <p>Cliente: {pedido.cliente_nombre}</p>
                <p style={{fontSize: '11px', color: '#666'}}>
                  {pedido.cliente_email} {pedido.cliente_telefono && `| ${pedido.cliente_telefono}`}
                </p>
              </div>
              <div className="pedido-estado">
                <span className="estado-badge" style={{ backgroundColor: getEstadoColor(pedido.estado_pedido) }}>
                  {getEstadoTexto(pedido.estado_pedido)}
                </span>
                <p className="pedido-total">${Number(pedido.total).toFixed(2)}</p>
                <p style={{fontSize: '11px', marginTop: '4px'}}>Pago: {pedido.estado_pago}</p>
              </div>
            </div>

            <div className="pedido-productos">
              <h4>Productos ({pedido.productos.length}):</h4>
              <ul>
                {pedido.productos.slice(0, 3).map((producto, index) => (
                  <li key={index}>
                    {producto.variant_color && (
                      <span style={{
                        display: 'inline-block',
                        width: 14,
                        height: 14,
                        borderRadius: 3,
                        background: producto.variant_color,
                        border: '1px solid #ccc',
                        marginRight: 6,
                        verticalAlign: 'middle'
                      }} />
                    )}
                    {producto.cantidad}x {producto.concepto}
                    {producto.variant_color && <span style={{fontSize: '11px', color: '#666'}}> ({producto.variant_color})</span>}
                    {producto.variant_talla && <span style={{fontSize: '11px', color: '#666'}}> - Talla: {producto.variant_talla}</span>}
                    {' - '}${(producto.precio_unitario * producto.cantidad).toFixed(2)}
                  </li>
                ))}
                {pedido.productos.length > 3 && <li>... y {pedido.productos.length - 3} más</li>}
              </ul>
            </div>

            <div className="pedido-detalles">
              <p><strong>Método de pago:</strong> {pedido.metodo_pago}</p>
              {pedido.referencia_pago && <p><strong>Referencia:</strong> {pedido.referencia_pago}</p>}
              {pedido.datos_envio && (
                <p><strong>Dirección:</strong> {pedido.datos_envio.direccion || pedido.cliente_direccion}</p>
              )}
            </div>

            <div className="pedido-acciones">
              <button onClick={() => setPedidoSeleccionado(pedido)} className="btn-ver-detalle">
                Ver Detalle
              </button>

              {(usuario.rol === 'admin' || usuario.rol === 'bodega') && (
                <>
                  {pedido.estado_pedido === 'nuevo' && (
                    <button 
                      onClick={() => cambiarEstadoPedido(pedido.id_pedido, 'confirmado')} 
                      className="btn-procesar"
                      disabled={actualizando}
                    >
                      ✓ Confirmar
                    </button>
                  )}
                  {pedido.estado_pedido === 'confirmado' && (
                    <button 
                      onClick={() => cambiarEstadoPedido(pedido.id_pedido, 'preparando')} 
                      className="btn-procesar"
                      disabled={actualizando}
                    >
                      📦 Preparar
                    </button>
                  )}
                  {pedido.estado_pedido === 'preparando' && (
                    <button 
                      onClick={() => cambiarEstadoPedido(pedido.id_pedido, 'enviado')} 
                      className="btn-procesar"
                      disabled={actualizando}
                    >
                      🚚 Enviar
                    </button>
                  )}
                  {pedido.estado_pedido === 'enviado' && (
                    <button 
                      onClick={() => convertirAVenta(pedido.id_pedido)} 
                      className="btn-completar"
                      disabled={actualizando}
                    >
                      ✓ Marcar Entregado (Crear Venta)
                    </button>
                  )}
                  {['nuevo', 'confirmado', 'preparando'].includes(pedido.estado_pedido) && (
                    <button 
                      onClick={() => cancelarPedido(pedido.id_pedido)} 
                      className="btn-cancelar"
                      disabled={actualizando}
                    >
                      ✕ Cancelar
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        ))
        )}
      </div>

      {/* Modal de detalle pedido */}
      {pedidoSeleccionado && (
        <div className="modal-overlay">
          <div className="modal-detalle">
            <div className="modal-header">
              <h3>Detalle del Pedido #{pedidoSeleccionado.id_pedido}</h3>
              <button onClick={() => setPedidoSeleccionado(null)} className="btn-cerrar">✕</button>
            </div>

            <div className="modal-content">
              <div className="detalle-seccion">
                <h4>Información del Cliente</h4>
                <p><strong>Nombre:</strong> {pedidoSeleccionado.cliente_nombre}</p>
                <p><strong>Email:</strong> {pedidoSeleccionado.cliente_email}</p>
                {pedidoSeleccionado.cliente_telefono && (
                  <p><strong>Teléfono:</strong> {pedidoSeleccionado.cliente_telefono}</p>
                )}
                {pedidoSeleccionado.cliente_direccion && (
                  <p><strong>Dirección:</strong> {pedidoSeleccionado.cliente_direccion}</p>
                )}
              </div>

              <div className="detalle-seccion">
                <h4>Productos Solicitados</h4>
                <table className="productos-tabla">
                  <thead>
                    <tr>
                      <th>Producto</th>
                      <th>Color/Talla</th>
                      <th>Cantidad</th>
                      <th>Precio Unit.</th>
                      <th>Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pedidoSeleccionado.productos.map((producto, index) => (
                      <tr key={index}>
                        <td>{producto.concepto}</td>
                        <td>
                          {producto.variant_color ? (
                            <div style={{display: 'flex', alignItems: 'center', gap: 6}}>
                              <span style={{
                                display: 'inline-block',
                                width: 18,
                                height: 18,
                                borderRadius: 3,
                                background: producto.variant_color,
                                border: '1px solid #ccc'
                              }} />
                              <span style={{fontSize: '11px'}}>{producto.variant_color}</span>
                              {producto.variant_talla && <span style={{fontSize: '11px', marginLeft: 4}}>/ {producto.variant_talla}</span>}
                            </div>
                          ) : '—'}
                        </td>
                        <td>{producto.cantidad}</td>
                        <td>${Number(producto.precio_unitario).toFixed(2)}</td>
                        <td>${(producto.precio_unitario * producto.cantidad).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="detalle-seccion">
                <h4>Información del Pedido</h4>
                <p><strong>Fecha:</strong> {formatFecha(pedidoSeleccionado.fecha_creacion)}</p>
                <p><strong>Estado pedido:</strong> {getEstadoTexto(pedidoSeleccionado.estado_pedido)}</p>
                <p><strong>Estado pago:</strong> {pedidoSeleccionado.estado_pago}</p>
                <p><strong>Método de pago:</strong> {pedidoSeleccionado.metodo_pago}</p>
                {pedidoSeleccionado.referencia_pago && (
                  <p><strong>Referencia pago:</strong> {pedidoSeleccionado.referencia_pago}</p>
                )}
                <p><strong>Subtotal:</strong> ${Number(pedidoSeleccionado.subtotal).toFixed(2)}</p>
                {pedidoSeleccionado.envio > 0 && (
                  <p><strong>Envío:</strong> ${Number(pedidoSeleccionado.envio).toFixed(2)}</p>
                )}
                <p><strong>Total:</strong> ${Number(pedidoSeleccionado.total).toFixed(2)}</p>
                {pedidoSeleccionado.datos_envio && (
                  <>
                    <h4 style={{marginTop: '15px'}}>Datos de Envío</h4>
                    <pre style={{background: '#f5f5f5', padding: '10px', borderRadius: '4px', fontSize: '12px'}}>
                      {JSON.stringify(pedidoSeleccionado.datos_envio, null, 2)}
                    </pre>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
        </>
      ) : (
        <>
          {/* Vista de Cotizaciones E-commerce */}
          <div className="pedidos-filtros">
            <button onClick={cargarCotizaciones} className="btn-confirmar">
              🔄 Actualizar Cotizaciones
            </button>
          </div>

          <div className="pedidos-lista">
            {cotizaciones.length === 0 ? (
              <p style={{textAlign: 'center', padding: '20px', color: '#666'}}>
                No hay cotizaciones del e-commerce
              </p>
            ) : (
              cotizaciones.map(cotizacion => (
                <div key={cotizacion.id_cotizacion} className="pedido-card">
                  <div className="pedido-header">
                    <div className="pedido-info">
                      <h3>Cotización #{cotizacion.id_cotizacion}</h3>
                      <p>Fecha: {formatFecha(cotizacion.fecha)}</p>
                      <p>Cliente: {cotizacion.nombre}</p>
                      {cotizacion.empresa && <p style={{fontSize: '12px', color: '#666'}}>Empresa: {cotizacion.empresa}</p>}
                      <p style={{fontSize: '11px', color: '#666'}}>
                        {cotizacion.email} {cotizacion.telefono && `| ${cotizacion.telefono}`}
                      </p>
                    </div>
                    <div className="pedido-estado">
                      <span className="estado-badge" style={{ backgroundColor: getEstadoCotizacionColor(cotizacion.estado) }}>
                        {getEstadoCotizacionTexto(cotizacion.estado)}
                      </span>
                      <p className="pedido-total">${Number(cotizacion.total_estimated || 0).toFixed(2)}</p>
                      <p style={{fontSize: '11px', marginTop: '4px'}}>{cotizacion.total_items} items</p>
                    </div>
                  </div>

                  {cotizacion.notas && (
                    <div style={{padding: '10px 0', borderTop: '1px solid #eee', marginTop: '10px'}}>
                      <p style={{fontSize: '12px', color: '#666'}}><strong>Notas:</strong> {cotizacion.notas}</p>
                    </div>
                  )}

                  <div className="pedido-acciones">
                    <button onClick={() => setCotizacionSeleccionada(cotizacion)} className="btn-ver-detalle">
                      Ver Detalle
                    </button>

                    {(usuario.rol === 'admin' || usuario.rol === 'bodega') && (
                      <>
                        {cotizacion.estado === 'pendiente' && (
                          <button 
                            onClick={() => actualizarEstadoCotizacion(cotizacion.id_cotizacion, 'contactado')} 
                            className="btn-procesar"
                            disabled={actualizando}
                          >
                            📞 Marcar Contactado
                          </button>
                        )}
                        {(cotizacion.estado === 'pendiente' || cotizacion.estado === 'contactado') && (
                          <>
                            <button 
                              onClick={() => actualizarEstadoCotizacion(cotizacion.id_cotizacion, 'convertido')} 
                              className="btn-completar"
                              disabled={actualizando}
                            >
                              ✓ Convertido
                            </button>
                            <button 
                              onClick={() => actualizarEstadoCotizacion(cotizacion.id_cotizacion, 'rechazado')} 
                              className="btn-cancelar"
                              disabled={actualizando}
                            >
                              ✕ Rechazar
                            </button>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Modal de detalle cotización */}
          {cotizacionSeleccionada && (
            <div className="modal-overlay">
              <div className="modal-detalle">
                <div className="modal-header">
                  <h3>Detalle de Cotización #{cotizacionSeleccionada.id_cotizacion}</h3>
                  <button onClick={() => setCotizacionSeleccionada(null)} className="btn-cerrar">✕</button>
                </div>

                <div className="modal-content">
                  <div className="detalle-seccion">
                    <h4>Información del Cliente</h4>
                    <p><strong>Nombre:</strong> {cotizacionSeleccionada.nombre}</p>
                    {cotizacionSeleccionada.empresa && <p><strong>Empresa:</strong> {cotizacionSeleccionada.empresa}</p>}
                    <p><strong>Email:</strong> {cotizacionSeleccionada.email}</p>
                    {cotizacionSeleccionada.telefono && (
                      <p><strong>Teléfono:</strong> {cotizacionSeleccionada.telefono}</p>
                    )}
                  </div>

                  {cotizacionSeleccionada.productos && cotizacionSeleccionada.productos.length > 0 && (
                    <div className="detalle-seccion">
                      <h4>Productos Solicitados</h4>
                      <table className="productos-tabla">
                        <thead>
                          <tr>
                            <th>Producto</th>
                            <th>Cantidad</th>
                            <th>Precio Unit.</th>
                            <th>Subtotal</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cotizacionSeleccionada.productos.map((producto, index) => (
                            <tr key={index}>
                              <td>
                                {producto.producto_nombre}
                                {producto.notas && <div style={{fontSize: '11px', color: '#666', marginTop: '4px'}}>{producto.notas}</div>}
                              </td>
                              <td>{producto.cantidad}</td>
                              <td>${Number(producto.precio_unitario || 0).toFixed(2)}</td>
                              <td>${((producto.precio_unitario || 0) * producto.cantidad).toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <div className="detalle-seccion">
                    <h4>Información de la Cotización</h4>
                    <p><strong>Fecha:</strong> {formatFecha(cotizacionSeleccionada.fecha)}</p>
                    <p><strong>Estado:</strong> {getEstadoCotizacionTexto(cotizacionSeleccionada.estado)}</p>
                    <p><strong>Total items:</strong> {cotizacionSeleccionada.total_items}</p>
                    <p><strong>Total estimado:</strong> ${Number(cotizacionSeleccionada.total_estimated || 0).toFixed(2)}</p>
                    {cotizacionSeleccionada.notas && (
                      <>
                        <h4 style={{marginTop: '15px'}}>Notas Adicionales</h4>
                        <p style={{background: '#f5f5f5', padding: '10px', borderRadius: '4px', fontSize: '12px'}}>
                          {cotizacionSeleccionada.notas}
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default Pedidos;
